import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  startAfter,
  Timestamp,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type {
  ServiceHistoryEntry,
  ServiceHistoryFilters,
  ServiceHistoryStats,
  GroupedServiceHistory,
  PaginationState,
  SortConfig,
  UseServiceHistoryReturn,
} from '@/types/serviceHistory';

interface UseServiceHistoryProps {
  barberId?: string;
  branchId?: string;
  franchiseId?: string;
  initialPageSize?: number;
  autoLoad?: boolean;
}

export function useServiceHistory({
  barberId,
  branchId,
  franchiseId,
  initialPageSize = 30,
  autoLoad = true,
}: UseServiceHistoryProps): UseServiceHistoryReturn {
  // State
  const [entries, setEntries] = useState<ServiceHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Filters
  const [filters, setFiltersState] = useState<ServiceHistoryFilters>({
    dateRange: {
      start: getDefaultStartDate(), // Last 30 days
      end: new Date(),
    },
  });

  // Pagination
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    pageSize: initialPageSize,
    hasMore: false,
    lastVisible: null,
  });

  // Sorting
  // IMPORTANT: Must use 'updatedAt' to match the date range filter field
  // Firestore requires sorting by the same field used in range queries
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'updatedAt',
    order: 'desc',
  });

  // Load initial data
  useEffect(() => {
    if (autoLoad && (barberId || branchId || franchiseId)) {
      loadHistory();
    }
  }, [barberId, branchId, franchiseId, filters, sortConfig]);

  // Build Firestore query
  const buildQuery = useCallback(
    (isLoadMore = false) => {
      // IMPORTANT: Completed tickets are archived in 'queueArchive' collection
      // Not in 'queues' which only contains active/pending tickets
      let q = query(collection(db, 'queueArchive'));

      // Apply role-based filtering
      if (barberId) {
        q = query(q, where('barberId', '==', barberId));
      } else if (branchId) {
        q = query(q, where('branchId', '==', branchId));
      } else if (franchiseId) {
        q = query(q, where('franchiseId', '==', franchiseId));
      }

      // Filter by status (completed, cancelled, no_show)
      const statusFilter = filters.status || ['completed', 'cancelled', 'no_show'];
      if (statusFilter.length === 1) {
        q = query(q, where('status', '==', statusFilter[0]));
      } else {
        q = query(q, where('status', 'in', statusFilter));
      }

      // Date range filter
      // IMPORTANT: Use 'updatedAt' instead of 'completedAt' because:
      // - 'completedAt' only exists on tickets with status 'completed'
      // - 'cancelled' and 'no_show' tickets don't have 'completedAt'
      // - 'updatedAt' exists on all tickets regardless of status
      const startDate = new Date(filters.dateRange.start);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(filters.dateRange.end);
      endDate.setHours(23, 59, 59, 999);

      q = query(
        q,
        where('updatedAt', '>=', Timestamp.fromDate(startDate)),
        where('updatedAt', '<=', Timestamp.fromDate(endDate))
      );

      // Service filter
      if (filters.serviceId) {
        q = query(q, where('serviceId', '==', filters.serviceId));
      }

      // Branch filter (for multi-branch barbers/admins)
      if (filters.branchId && !branchId) {
        q = query(q, where('branchId', '==', filters.branchId));
      }

      // Sorting
      q = query(q, orderBy(sortConfig.field, sortConfig.order));

      // Pagination
      if (isLoadMore && pagination.lastVisible) {
        q = query(q, startAfter(pagination.lastVisible));
      }

      q = query(q, limit(pagination.pageSize + 1));

      return q;
    },
    [barberId, branchId, franchiseId, filters, sortConfig, pagination]
  );

  // Load history
  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 useServiceHistory: Loading history', {
        barberId,
        branchId,
        franchiseId,
        filters,
        sortConfig,
      });

      const q = buildQuery(false);
      const snapshot = await getDocs(q);

      console.log('📊 useServiceHistory: Query result', {
        totalDocs: snapshot.docs.length,
        hasMore: snapshot.docs.length > pagination.pageSize,
        sampleDocs: snapshot.docs.slice(0, 3).map(d => ({
          id: d.id,
          status: d.data().status,
          completedAt: d.data().completedAt?.toDate?.()?.toISOString(),
          updatedAt: d.data().updatedAt?.toDate?.()?.toISOString(),
          barberId: d.data().barberId,
        })),
      });

      const hasMore = snapshot.docs.length > pagination.pageSize;
      const docs = hasMore ? snapshot.docs.slice(0, pagination.pageSize) : snapshot.docs;

      const newEntries = docs.map(mapDocToEntry);

      console.log('✅ useServiceHistory: Entries mapped', {
        entriesCount: newEntries.length,
        sampleEntries: newEntries.slice(0, 3).map(e => ({
          queueId: e.queueId,
          status: e.status,
          clientName: e.clientName,
          completedAt: e.completedAt?.toDate?.()?.toISOString(),
        })),
      });

      setEntries(newEntries);
      setPagination({
        ...pagination,
        hasMore,
        lastVisible: docs[docs.length - 1] || null,
        page: 0,
      });
    } catch (err) {
      console.error('❌ useServiceHistory: Error loading service history:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  // Load more
  const loadMore = async () => {
    if (!pagination.hasMore || loadingMore) return;

    try {
      setLoadingMore(true);

      const q = buildQuery(true);
      const snapshot = await getDocs(q);

      const hasMore = snapshot.docs.length > pagination.pageSize;
      const docs = hasMore ? snapshot.docs.slice(0, pagination.pageSize) : snapshot.docs;

      const newEntries = docs.map(mapDocToEntry);

      setEntries([...entries, ...newEntries]);
      setPagination({
        ...pagination,
        hasMore,
        lastVisible: docs[docs.length - 1] || null,
        page: pagination.page + 1,
      });
    } catch (err) {
      console.error('Error loading more:', err);
      setError(err as Error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Map Firestore doc to ServiceHistoryEntry
  const mapDocToEntry = (doc: QueryDocumentSnapshot): ServiceHistoryEntry => {
    const data = doc.data();

    // Use completedAt if available, otherwise use updatedAt as fallback
    // - 'completed' tickets have completedAt
    // - 'cancelled' and 'no_show' tickets don't have completedAt, use updatedAt
    const eventTimestamp = data.completedAt || data.cancelledAt || data.updatedAt;

    // Generate fallback ticket number if not present
    let ticketNumber = data.ticketNumber;
    if (!ticketNumber) {
      // Generate number from date + last 4 chars of doc ID
      const date = eventTimestamp?.toDate() || new Date();
      const dateStr = date.toISOString().split('T')[0].replace(/-/g, ''); // YYYYMMDD
      const docIdSuffix = doc.id.substring(doc.id.length - 4).toUpperCase();
      ticketNumber = `T-${dateStr}-${docIdSuffix}`;
    }

    return {
      queueId: doc.id,
      ticketNumber,
      userId: data.userId,
      clientName: data.clientName || data.clientEmail || 'Cliente',
      clientEmail: data.clientEmail,
      clientType: data.clientType,
      serviceId: data.serviceId,
      serviceName: data.serviceName,
      serviceCategory: data.serviceCategory,
      serviceDuration: data.actualServiceDuration || data.serviceDuration || 0,
      servicePrice: data.servicePrice || data.amountToPay || 0,
      amountCharged: data.amountToPay || 0,
      discountApplied: data.discountApplied || 0,
      loyaltyPointsEarned: data.pointsEarned || 0,
      usedStampCard: data.useStampCard || false,
      completedAt: eventTimestamp, // Use fallback timestamp for cancelled/no_show
      serviceStartedAt: data.serviceStartedAt,
      status: data.status,
      cancelReason: data.cancelReason,
      branchId: data.branchId,
      branchName: data.branchName,
    };
  };

  // Group entries by date
  const groupedEntries = useMemo<GroupedServiceHistory[]>(() => {
    const grouped = new Map<string, ServiceHistoryEntry[]>();

    entries.forEach((entry) => {
      const date = entry.completedAt.toDate().toISOString().split('T')[0];
      if (!grouped.has(date)) {
        grouped.set(date, []);
      }
      grouped.get(date)!.push(entry);
    });

    return Array.from(grouped.entries())
      .map(([date, entries]) => ({
        date,
        entries,
        dailyStats: {
          totalServices: entries.length,
          totalEarnings: entries.reduce((sum, e) => sum + e.amountCharged, 0),
          totalDuration: entries.reduce((sum, e) => sum + e.serviceDuration, 0),
        },
      }))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [entries]);

  // Calculate statistics
  const stats = useMemo<ServiceHistoryStats>(() => {
    const completed = entries.filter((e) => e.status === 'completed');
    const cancelled = entries.filter((e) => e.status === 'cancelled');
    const noShows = entries.filter((e) => e.status === 'no_show');

    const totalEarnings = completed.reduce((sum, e) => sum + e.amountCharged, 0);
    const totalDuration = completed.reduce((sum, e) => sum + e.serviceDuration, 0);
    const totalDiscounts = completed.reduce((sum, e) => sum + e.discountApplied, 0);

    // Count unique clients
    const uniqueClients = new Set(entries.map((e) => e.userId)).size;
    const newClients = entries.filter((e) => e.clientType === 'first_time').length;
    const vipClients = entries.filter((e) => e.clientType === 'vip').length;

    // Find most popular service
    const serviceCounts = new Map<string, { name: string; count: number }>();
    completed.forEach((e) => {
      // Try with serviceId first (preferred)
      if (e.serviceId && e.serviceName) {
        const current = serviceCounts.get(e.serviceId) || { name: e.serviceName, count: 0 };
        serviceCounts.set(e.serviceId, { name: current.name, count: current.count + 1 });
      }
      // If no serviceId but has serviceName, use name as key
      else if (e.serviceName) {
        const key = `name_${e.serviceName}`;
        const current = serviceCounts.get(key) || { name: e.serviceName, count: 0 };
        serviceCounts.set(key, { name: current.name, count: current.count + 1 });
      }
      // If neither, count as "Servicio General"
      else {
        const key = 'general_service';
        const current = serviceCounts.get(key) || { name: 'Servicio General', count: 0 };
        serviceCounts.set(key, { name: current.name, count: current.count + 1 });
      }
    });

    let mostPopularService: { serviceId: string; serviceName: string; count: number } | undefined;
    serviceCounts.forEach((value, key) => {
      if (!mostPopularService || value.count > mostPopularService.count) {
        mostPopularService = { serviceId: key, serviceName: value.name, count: value.count };
      }
    });

    return {
      totalServices: entries.length,
      completedServices: completed.length,
      cancelledServices: cancelled.length,
      noShowServices: noShows.length,
      totalEarnings,
      averageEarnings: completed.length > 0 ? Math.round(totalEarnings / completed.length) : 0,
      totalDiscounts,
      averageDuration: completed.length > 0 ? Math.round(totalDuration / completed.length) : 0,
      totalDuration,
      uniqueClients,
      newClients,
      vipClients,
      returningClients: uniqueClients - newClients,
      mostPopularService,
      // TODO: Implement comparison with previous period
      comparison: undefined,
    };
  }, [entries]);

  // Filter setters
  const setFilters = useCallback((newFilters: Partial<ServiceHistoryFilters>) => {
    setFiltersState((prev) => ({ ...prev, ...newFilters }));
    setPagination((prev) => ({ ...prev, page: 0, lastVisible: null }));
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState({
      dateRange: {
        start: getDefaultStartDate(),
        end: new Date(),
      },
    });
  }, []);

  // Export functions
  const exportToPDF = async () => {
    try {
      // Dynamic import to reduce bundle size
      const jsPDF = (await import('jspdf')).default;
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF();

      // Header
      doc.setFontSize(20);
      doc.setTextColor(37, 99, 235); // Blue color
      doc.text('Historial de Servicios', 14, 20);

      // Date range
      doc.setFontSize(11);
      doc.setTextColor(100, 100, 100);
      const startStr = filters.dateRange.start.toLocaleDateString('es-ES');
      const endStr = filters.dateRange.end.toLocaleDateString('es-ES');
      doc.text(`Período: ${startStr} - ${endStr}`, 14, 28);

      // Statistics Summary Box
      doc.setFillColor(240, 240, 255);
      doc.rect(14, 35, 182, 35, 'F');

      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text('RESUMEN ESTADÍSTICO', 16, 42);

      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);

      // First row of stats
      doc.text(`Total Servicios: ${stats.totalServices}`, 16, 50);
      doc.text(`Completados: ${stats.completedServices}`, 70, 50);
      doc.text(`Cancelados: ${stats.cancelledServices}`, 120, 50);

      // Second row of stats
      doc.text(`Ingresos Totales: €${(stats.totalEarnings / 100).toFixed(2)}`, 16, 57);
      doc.text(`Promedio: €${(stats.averageEarnings / 100).toFixed(2)}`, 70, 57);
      doc.text(`Duración Promedio: ${stats.averageDuration}m`, 120, 57);

      // Third row
      if (stats.mostPopularService) {
        doc.text(`Servicio Popular: ${stats.mostPopularService.serviceName}`, 16, 64);
        doc.text(`(${stats.mostPopularService.count} veces)`, 95, 64);
      }

      // Prepare table data
      const tableData = entries.map((entry) => {
        const date = entry.completedAt.toDate();
        return [
          date.toLocaleDateString('es-ES'),
          date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
          entry.ticketNumber,
          entry.clientName,
          entry.serviceName || 'Servicio General',
          `${entry.serviceDuration}m`,
          `€${(entry.amountCharged / 100).toFixed(2)}`,
          entry.status === 'completed' ? '✓' : entry.status === 'cancelled' ? 'X' : 'NS',
        ];
      });

      // Add table
      autoTable(doc, {
        startY: 75,
        head: [['Fecha', 'Hora', 'Ticket', 'Cliente', 'Servicio', 'Duración', 'Monto', 'Estado']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [37, 99, 235],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9,
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [50, 50, 50],
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250],
        },
        columnStyles: {
          0: { cellWidth: 22 }, // Fecha
          1: { cellWidth: 18 }, // Hora
          2: { cellWidth: 20 }, // Ticket
          3: { cellWidth: 35 }, // Cliente
          4: { cellWidth: 35 }, // Servicio
          5: { cellWidth: 18 }, // Duración
          6: { cellWidth: 20 }, // Monto
          7: { cellWidth: 14, halign: 'center' }, // Estado
        },
        margin: { top: 75, left: 14, right: 14 },
        didDrawPage: (data: any) => {
          // Footer on each page
          const pageCount = (doc as any).internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(150, 150, 150);
          doc.text(
            `Página ${data.pageNumber} de ${pageCount}`,
            (doc as any).internal.pageSize.width / 2,
            (doc as any).internal.pageSize.height - 10,
            { align: 'center' as any }
          );

          // Date generated
          doc.text(
            `Generado: ${new Date().toLocaleString('es-ES')}`,
            14,
            (doc as any).internal.pageSize.height - 10
          );
        },
      } as any);

      // Save the PDF
      const fileName = `historial-servicios-${filters.dateRange.start.toISOString().split('T')[0]}-${filters.dateRange.end.toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error al generar el PDF. Por favor, intenta de nuevo.');
    }
  };

  const exportToCSV = async () => {
    const headers = [
      'Fecha',
      'Hora',
      'Ticket',
      'Cliente',
      'Email',
      'Servicio',
      'Duración (min)',
      'Precio (€)',
      'Descuento (€)',
      'Puntos',
      'Estado',
    ];

    const rows = entries.map((entry) => {
      const date = entry.completedAt.toDate();
      return [
        date.toLocaleDateString('es-ES'),
        date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        entry.ticketNumber,
        entry.clientName,
        entry.clientEmail || '',
        entry.serviceName || '',
        entry.serviceDuration.toString(),
        (entry.amountCharged / 100).toFixed(2),
        (entry.discountApplied / 100).toFixed(2),
        entry.loyaltyPointsEarned.toString(),
        entry.status,
      ];
    });

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `historial-servicios-${new Date().toISOString()}.csv`);
    link.click();
  };

  return {
    entries,
    groupedEntries,
    stats,
    loading,
    loadingMore,
    error,
    pagination,
    loadMore,
    filters,
    setFilters,
    clearFilters,
    sortConfig,
    setSortConfig,
    refresh: loadHistory,
    exportToPDF,
    exportToCSV,
  };
}

// Helper function
function getDefaultStartDate(): Date {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date;
}
